import React, { ReactNode } from 'react';
import { Location } from 'history';
import I18n from 'i18n-js';
import { gql } from '@reverbdotcom/commons/src/gql';
import RQLSearchGrid, { PaginationMode } from '@reverbdotcom/commons/src/components/rql_search_grid';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { inMobileApp } from '@reverbdotcom/commons/src/user_context_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import {
  reverb_search_CSPSearchRequest_Sort,
  reverb_search_CSPSearchRequest_Aggregation,
  reverb_search_FullTextQueryOperand,
  Core_SellFlow_SearchQuery,
  Core_SellFlow_SearchQueryVariables,
  ExpressSaleItemBidDataFragment,
  SellCardDataFragment,
} from '@reverbdotcom/commons/src/gql/graphql';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { convertMoney, EXCHANGE_RATES_QUERY } from '@reverbdotcom/commons/src/convert_currency';
import { conditionUuidForSlug, GOOD, MINT } from '@reverbdotcom/commons/src/condition_helpers';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';

import { EVERYWHERE_CODE } from '../shared/constants';
import URLHelpers, { Paths } from '../shared/url_helpers';
import uuid from '../../lib/uuid';
import SellCard from './sell_card';
import CSPSelectorCard from '../gear_collections/csp_search/CSPSelectorCard';
import BailCard from './bail_card';
import searchEmptyStateImage from '../../../images/icons/empty-states/not-found.svg';
import { US } from '@reverbdotcom/commons/src/constants';

export interface renderGridItemsParams {
  csps: (SellCardDataFragment & ExpressSaleItemBidDataFragment)[];
  loading: boolean;
  location?: Location;
}

interface IProps {
  location: Location;
  renderGridItems?: ({ csps, loading, location }: renderGridItemsParams) => React.ReactNode[];
  renderEmptyLink?: () => React.ReactNode;
  promotionalToken?: string;
  filterNames?: string[];
  scrollTarget?: ScrollTarget;
  forGearCollection?: boolean;
  setSelectedCSP?: (value) => void;
}

const COMPONENT_NAME = 'SellGrid';

export const SELL_CARD_LIMIT = 9;
export const SELL_GRID_EXCLUDED_CATEGORY_UUIDS = [
  '7681b711-435c-4923-bdc3-65076d15d78c', // Books and DVDs
  '98a45e2d-2cc2-4b17-b695-a5d198c8f6d3', // Picks
  '4ca6d5e9-f00f-468d-bcae-8c7497537281', // Tools
  '22af0079-d5e7-48d1-9e5c-108105a2156c', // Merchandise
];

export const DEFAULT_FILTER_NAMES = [
  reverb_search_CSPSearchRequest_Aggregation.CATEGORY_UUIDS,
  reverb_search_CSPSearchRequest_Aggregation.FINISHES,
  reverb_search_CSPSearchRequest_Aggregation.DECADES,
];

export function blankSellFormUrl(promotionalToken?: string, useLegacySellFormRoute = false, noResults = false): string {
  // the native iOS and Android apps still need to use the classic sell form routes to support app versions from earlier than September 2022
  if (useLegacySellFormRoute) {
    return `/sell?blank${promotionalToken ? `&promotional_token=${promotionalToken}` : ''}`;
  }

  const params = {} as any;

  if (promotionalToken) {
    params.promotional_token = promotionalToken;
  }

  if (noResults) {
    params.no_results = true;
  }

  return Paths.sellFormNewListing.expand({ query: params });
}

function getTipText(location: Location): string | null {
  const wordCount = location.query?.query?.match(/\S+/g)?.length;
  if (wordCount === 1) {
    return I18n.t('discovery.sellFlow.search.tip');
  }
  return null;
}

function getTotalText(hasCspResults: boolean, location: Location): string | null {
  if (!hasCspResults || !location.query.query) return null;
  return I18n.t('discovery.sellFlow.search.resultsFor', { query: location.query.query });
}

export function EmptyMessage({ location, emptyLink }: { location: Location, emptyLink: ReactNode }) {
  return (
    <section className="sell__empty">
      <div className="sell__empty-content">
        <h1>
          <SanitizedRender
            allowedTags={['strong']}
            html={I18n.t('discovery.sellFlow.search.emptyMessage', {
              query: location.query?.query || '',
            })}
          />
        </h1>
        <h2>
          <SanitizedRender html={I18n.t('discovery.sellFlow.search.queryTip')} />
        </h2>
        <p>
          {I18n.t('discovery.sellFlow.search.cantFindIt')}
        </p>
        {emptyLink}
        <div className="sell__empty-content-image">
          <img
            src={searchEmptyStateImage}
            alt={I18n.t('discovery.sellFlow.search.emptyImageAlt')}
          />
        </div>
      </div>
    </section>
  );
}

export default function SellGrid({
  location,
  promotionalToken = '',
  filterNames = DEFAULT_FILTER_NAMES,
  renderGridItems = undefined,
  renderEmptyLink = undefined,
  forGearCollection = false,
  setSelectedCSP = undefined,
}: IProps) {
  const user = useUser();
  const queryOptions = getSellGridQueryOptions(location, forGearCollection);

  const {
    loading,
    data,
    previousData,
  } = useQuery<Core_SellFlow_SearchQuery, Core_SellFlow_SearchQueryVariables>(
    SELL_GRID_QUERY,
    queryOptions,
  );

  const { data: exchangeRatesData } = useQuery(EXCHANGE_RATES_QUERY);
  const exchangeRates = exchangeRatesData?.exchangeRates;
  const safeData = data ?? previousData;
  const cspSearch = safeData?.cspSearch;
  const shouldRender = !!safeData && !!cspSearch;
  const hasCspResults = shouldRender && cspSearch?.csps?.length > 0;
  const shouldUseLegacySellFormRoute = inMobileApp(user);

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
  }, true);

  if (!shouldRender) return null;

  function renderGridItem(csp, forGearCollection = false) {
    const priceRecommendations = csp.priceRecommendations || [];
    const goodPriceRecommendation = priceRecommendations.find((recommendation) => recommendation?.conditionUuid == conditionUuidForSlug(GOOD));
    const mintPriceRecommendation = priceRecommendations.find((recommendation) => recommendation?.conditionUuid == conditionUuidForSlug(MINT));
    const lowPriceRecommendation = goodPriceRecommendation && convertMoney(goodPriceRecommendation.priceLow, user.currency, exchangeRates, { precision: 0 });
    const highPriceRecommendation = mintPriceRecommendation && convertMoney(mintPriceRecommendation.priceHigh, user.currency, exchangeRates, { precision: 0 });

    let card;

    if (forGearCollection) {
      const { id, canonicalProductIds } = csp;
      const hasSingleVariant = canonicalProductIds.length === 1;

      const href = hasSingleVariant ? URLHelpers.gearCollectionItemFormNewPath({ cspId: id, cspVariantId: canonicalProductIds[0] }) : null;

      const handleClick = hasSingleVariant ? null : (value) => setSelectedCSP(value);

      card = (
        <CSPSelectorCard
          active={location.query.query === csp.title}
          key={csp.id}
          csp={csp}
          loading={loading}
          handleClick={handleClick}
          href={href}
          lowPriceRecommendation={lowPriceRecommendation}
          highPriceRecommendation={highPriceRecommendation}
        />
      );
    } else {
      card = (
        <SellCard
          active={location.query.query === csp.title}
          key={csp.id}
          csp={csp}
          loading={loading}
          promotionalToken={promotionalToken}
          useLegacySellFormRoute={shouldUseLegacySellFormRoute}
          lowPriceRecommendation={lowPriceRecommendation}
          highPriceRecommendation={highPriceRecommendation}
        />
      );
    }

    return card;
  }

  function gridItems() {
    if (!shouldRender) return null;
    const { csps } = cspSearch;
    let cspCards;

    if (renderGridItems) {
      cspCards = renderGridItems({ csps, loading, location });
    } else if (forGearCollection) {
      cspCards = csps.map(csp => {
        return (
          <div className="d-flex" key={csp.id}>
            {renderGridItem(csp, forGearCollection)}
          </div>
        );
      });

      if (csps.length > 0) {
        cspCards.push(
          <div className="d-flex" key="bail-card">
            <BailCard
              key="bail_card"
              href={URLHelpers.gearCollectionItemFormNewPath({ title: location.query.query })}
              linkText={I18n.t('discovery.gearCollections.cspSearch.addFromScratchLink')}
              text={I18n.t('discovery.gearCollections.cspSearch.addFromScratchText')}
            />,
          </div>,
        );
      }
    } else {
      cspCards = csps.map(csp => {
        return (
          <div className="sell__product-grid__item" key={csp.id}>
            {renderGridItem(csp)}
          </div>
        );
      });

      const totalResults = cspCards.length;

      if (totalResults > 0) {
        cspCards.push((
          <div className="sell__product-grid__item" key="bail-card">
            <BailCard
              key={uuid()}
              href={blankSellFormUrl(promotionalToken, shouldUseLegacySellFormRoute)}
            />
          </div>
        ));
      }
    }

    return cspCards;
  }

  function renderCSPGrid() {
    if (!loading && !cspSearch?.csps?.length) {
      return (
        <EmptyMessage
          location={location}
          emptyLink={getEmptyLink()}
        />
      );
    }
    return (
      <div className="sell__product-grid">
        {gridItems()}
      </div>
    );
  }

  function getFilters() {
    const filter_names = filterNames;

    return cspSearch.filters.filter(
      f => filter_names.includes(f.aggregationName) || f.key === 'TEXT_QUERY',
    );
  }

  function getEmptyLink() {
    return renderEmptyLink ? renderEmptyLink() : (
      <CoreLink to={blankSellFormUrl(promotionalToken, shouldUseLegacySellFormRoute)}>
        {I18n.t('discovery.sellFlow.search.bailButton')}
      </CoreLink>
    );
  }
  // search object must be initial query or updated query from load more if present
  return (
    <RQLSearchGrid
      loading={loading}
      search={cspSearch}
      cards={renderCSPGrid()}
      location={location}
      paginationMode={PaginationMode.PAGINATED}
      hasSearchOverview
      tipText={getTipText(location)}
      totalText={getTotalText(hasCspResults, location)}
      filters={getFilters()}
      resultsTotal={cspSearch?.total}
    />
  );
}

export const SELL_GRID_QUERY = gql(`
  query Core_SellFlow_Search(
    $q: String
    $decades: [String]
    $finishes: [String]
    $brandNames: [String]
    $category_uuids: [String]
    $sellCardLimit: Int
    $excludedCategoryUuids: [String]
    $boostByClicks: Boolean
    $fullTextQueryOperand: reverb_search_FullTextQueryOperand
    $sort: reverb_search_CSPSearchRequest_Sort
    $fuzzy: Boolean
    $offset: Int
    $listingsThatShipTo: String
    $hasExpressSaleBid: Boolean!
    $includePriceRecommendations: Boolean!
    $priceRecommendationCountryCode: String
  ) {
    cspSearch(
      input: {
        fullTextQuery: $q
        decades: $decades
        finishes: $finishes
        brandNames: $brandNames
        categoryUuids: $category_uuids
        withAggregations: [CATEGORY_UUIDS, FINISHES, DECADES, BRAND_NAMES]
        excludedCategoryUuids: $excludedCategoryUuids
        limit: $sellCardLimit
        offset: $offset
        boostByClicks: $boostByClicks
        fullTextQueryOperand: $fullTextQueryOperand
        sort: $sort
        fuzzy: $fuzzy
        listingsThatShipTo: $listingsThatShipTo
        hasExpressSaleBid: $hasExpressSaleBid
      }
    ) {
      filters {
        ... FlatFilter
      }
      csps {
        _id
        ... SellCardData
        ... CSPPriceRecommendationData @include(if: $includePriceRecommendations)
        ... ExpressSaleItemBidData @include(if: $hasExpressSaleBid)
      }
      total
      offset
      limit
    }
  }
`);

function getSellGridQueryOptions(location: Location, forGearCollection: boolean) {
  const shouldSkip = () => {
    if (location.query.runDefaultQuery) { return false; }

    return !location.query.query;
  };

  const offset = location.query.page ? (parseInt(location.query.page, 10) - 1) * SELL_CARD_LIMIT : 0;

  return {
    skip: shouldSkip(),
    variables: {
      offset,
      sellCardLimit: SELL_CARD_LIMIT,
      q: location.query.query,
      decades: location.query.decades,
      finishes: location.query.finishes,
      brandNames: location.query.brand_names,
      category_uuids: location.query.category_uuids,
      excludedCategoryUuids: SELL_GRID_EXCLUDED_CATEGORY_UUIDS,
      fullTextQueryOperand: reverb_search_FullTextQueryOperand.AND,
      sort: reverb_search_CSPSearchRequest_Sort.RECENT_ORDERS_COUNT_USED_DESC,
      fuzzy: true,
      listingsThatShipTo: EVERYWHERE_CODE,
      hasExpressSaleBid: !!location.query.hasExpressSaleBid,
      includePriceRecommendations: true,
      priceRecommendationCountryCode: forGearCollection ? US : null,
    },
  };
}
